import { useState, useCallback, useEffect, useMemo, useRef, CSSProperties } from "react"
import { APIError } from "../../../../../app/modules/auth"
import { getAllPassareAcquaintances, PassareAcquaintance } from "../../../../../mogul/core/_requests"
import { SyncLeadButton } from "../DebugAllLeads"
import { formatLeadItemDate } from "../DebugPipedrive"
import { Link, Routes, Route, Navigate, useNavigate } from "react-router-dom"
import PassareTableHeader from "./passare-header/PassareTableHeader"
import { PassareFilterObject } from "./passare-header/PassareFilterList"
import PassareManagement from "../../../../../mogul/routes/passare/PassareManagement"
import PassareOrganizationDetail from "../../../../../mogul/routes/passare/detail/PassareOrganizationDetail"
import BulkSyncModal from "./BulkSyncModal"
import debounce from 'lodash.debounce'

const passareSaveFilterToLocalStorageKey = 'passare_acquaintances_table_filter_v1'

// Breathing room under the pane: enough to clear the card's bottom edge and
// leave the "more rows" chevron fully on screen.
const PANE_BOTTOM_GAP = 34
const PANE_MIN_HEIGHT = 320

// The leads table is both long (100 rows a page) and wider than the card on a
// laptop. Reading it meant scrolling back to the header to remember which
// column you were in. So the table body gets its own scroll pane and the header
// row pins to the top of it: because the header is part of the same table, it
// slides horizontally in exact step with the columns, so the labels above a
// value are always that value's labels.
const tableScrollPane: CSSProperties = {
    // Height is measured at runtime (see fitPaneToViewport) rather than guessed
    // with a vh calc — everything stacked above the pane changes height as the
    // window narrows and the tab row wraps. This is only the pre-measurement
    // fallback for the first paint.
    maxHeight: 'calc(100vh - 280px)',
    minHeight: '320px',
    overflow: 'auto',
    // Without this, swiping sideways past the end of the table hands the rest
    // of the gesture to the document, which slides the whole page under the
    // fixed sidebar. Keep a sideways gesture over the table on the table.
    overscrollBehaviorX: 'contain',
}

const stickyHeaderCell: CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    // Opaque, or the rows scroll through it. inset shadow rather than
    // border-bottom: Metronic's table is border-collapse: collapse, where a
    // sticky cell's own border does not travel with it.
    background: '#ffffff',
    boxShadow: 'inset 0 -1px 0 #e4e6ef',
}

const scrollNudgeButton: CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 3,
}

// The "more rows" control sits below the pane, centred. A fade alone can't
// carry this one: it tints whatever sits under it, so when the pane's bottom
// edge happens to land in the gap between two rows there is nothing to tint and
// the cue vanishes exactly when the table looks complete. A button is there
// either way.
const scrollNudgeButtonDown: CSSProperties = {
    position: 'absolute',
    left: '50%',
    bottom: '-0.9rem',
    transform: 'translateX(-50%)',
    zIndex: 3,
}

// Edge fades: the pane says which way it still has content. macOS hides overlay
// scrollbars at rest, so without these a full pane and a pane with 6,000 more
// rows below look identical. Decorative and click-through — the chevrons and the
// scroll gesture do the actual work.
const scrollFade = (side: 'bottom' | 'left' | 'right'): CSSProperties => {
    const towards = { bottom: 'to top', left: 'to right', right: 'to left' }[side]
    const base: CSSProperties = {
        position: 'absolute',
        zIndex: 1,
        pointerEvents: 'none',
        background: `linear-gradient(${towards}, #ffffff 0%, rgba(255, 255, 255, 0) 100%)`,
    }
    return side === 'bottom'
        ? { ...base, left: 0, right: 0, bottom: 0, height: '2.5rem' }
        : { ...base, top: 0, bottom: 0, [side]: 0, width: '2rem' }
}

// Isolated search box: it owns its own input value so that typing only
// re-renders THIS small component, never the heavy results table above. The
// parent is notified through a debounced, stable `onSearch` callback, so the
// expensive list re-render / API call happens at most once per 300ms of idle.
function PassareSearchBox({
    initialValue,
    onSearch,
}: {
    initialValue: string
    onSearch: (value: string) => void
}) {
    const [value, setValue] = useState(initialValue)
    const inputRef = useRef<HTMLInputElement>(null)

    // Always call the latest onSearch without recreating the debounced fn.
    const onSearchRef = useRef(onSearch)
    onSearchRef.current = onSearch

    const debouncedSearch = useMemo(
        () => debounce((searchValue: string) => {
            onSearchRef.current(searchValue)
        }, 300),
        []
    )

    // Cancel any pending debounced search if this box unmounts.
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

    const handleChange = (newValue: string) => {
        setValue(newValue)
        debouncedSearch(newValue)
    }

    const handleClear = () => {
        debouncedSearch.cancel()
        setValue('')
        onSearchRef.current('')
        // Keep focus in the field after clearing.
        inputRef.current?.focus()
    }

    return (
        <div className='d-flex align-items-center position-relative'>
            <span className='svg-icon svg-icon-1 position-absolute ms-6'>
                <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                    <path d='M21.7 18.9L18.6 15.8C17.9 16.9 16.9 17.9 15.8 18.6L18.9 21.7C19.3 22.1 19.9 22.1 20.3 21.7L21.7 20.3C22.1 19.9 22.1 19.3 21.7 18.9Z' fill='currentColor' />
                    <path opacity='0.3' d='M11 20C6 20 2 16 2 11C2 6 6 2 11 2C16 2 20 6 20 11C20 16 16 20 11 20ZM11 4C7.1 4 4 7.1 4 11C4 14.9 7.1 18 11 18C14.9 18 18 14.9 18 11C18 7.1 14.9 4 11 4ZM8 11C8 9.3 9.3 8 11 8C11.6 8 12 7.6 12 7C12 6.4 11.6 6 11 6C8.2 6 6 8.2 6 11C6 11.6 6.4 12 7 12C7.6 12 8 11.6 8 11Z' fill='currentColor' />
                </svg>
            </span>
            <input
                ref={inputRef}
                type='text'
                className='form-control form-control-solid w-300px ps-15'
                placeholder='Search by organization, deceased, contact info...'
                value={value}
                onChange={(e) => handleChange(e.target.value)}
                autoComplete="off"
            />
            {value && (
                <button
                    className='btn btn-flush btn-active-color-primary position-absolute end-0'
                    onClick={handleClear}
                    style={{ right: '10px' }}
                >
                    <span className='svg-icon svg-icon-2'>
                        <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                            <path opacity='0.3' d='M6 19.7C5.7 19.7 5.5 19.6 5.3 19.4C4.9 19 4.9 18.4 5.3 18L18 5.3C18.4 4.9 19 4.9 19.4 5.3C19.8 5.7 19.8 6.29999 19.4 6.69999L6.7 19.4C6.5 19.6 6.3 19.7 6 19.7Z' fill='currentColor' />
                            <path d='M18.8 19.7C18.5 19.7 18.3 19.6 18.1 19.4L5.40001 6.69999C5.00001 6.29999 5.00001 5.7 5.40001 5.3C5.80001 4.9 6.40001 4.9 6.80001 5.3L19.5 18C19.9 18.4 19.9 19 19.5 19.4C19.3 19.6 19 19.7 18.8 19.7Z' fill='currentColor' />
                        </svg>
                    </span>
                </button>
            )}
        </div>
    )
}

function PassareTable() {
    // const { currentUser } = useAuth()
    // const { accounts } = useLeadDetail()
    const navigate = useNavigate()

    // isAdmin is available but not used in this component
    // const isAdmin = !!currentUser?.is_admin

    const [currentFilterObject, setCurrentFilterObject] = useState<PassareFilterObject | null>(null)

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)

    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [errorLoading, setErrorLoading] = useState<string | null>(null);

    const [results, setResults] = useState<PassareAcquaintance[]>([])
    const [totalResults, setTotalResults] = useState(0)

    // Search states
    const [personSearchFilter, setPersonSearchFilter] = useState('');
    const isFirstRender = useRef(true);

    // Bulk sync states
    const [showBulkSyncModal, setShowBulkSyncModal] = useState(false);

    // Which edges still have table beyond them. macOS hides overlay scrollbars
    // at rest, so nothing otherwise distinguishes a full pane from one with
    // 6,000 more rows below or a Sync column just off to the right. These four
    // booleans drive the edge fades, the chevrons and the pinned header's lift.
    const tableScrollerRef = useRef<HTMLDivElement>(null)
    const [canScroll, setCanScroll] = useState({ left: false, right: false, up: false, down: false })

    useEffect(() => {
        const scroller = tableScrollerRef.current
        if (!scroller) return

        const update = () => {
            const maxX = scroller.scrollWidth - scroller.clientWidth
            const maxY = scroller.scrollHeight - scroller.clientHeight
            const next = {
                left: scroller.scrollLeft > 1,
                right: maxX > 1 && scroller.scrollLeft < maxX - 1,
                up: scroller.scrollTop > 1,
                down: maxY > 1 && scroller.scrollTop < maxY - 1,
            }
            // Return prev when nothing changed — this fires on every scroll
            // frame and the results table is expensive to re-render. Only the
            // four booleans flipping costs a render, not every pixel scrolled.
            setCanScroll(prev =>
                prev.left === next.left && prev.right === next.right &&
                prev.up === next.up && prev.down === next.down ? prev : next
            )
        }

        // Run the pane down to just above the fold. Measured, not guessed: the
        // page header, the tab row (which wraps to two lines as the window
        // narrows) and the card header all change height, and any fixed vh
        // calc is wrong for most of those combinations — it was leaving ~100px
        // of dead card below the table.
        const fitPaneToViewport = () => {
            // Document-relative top, so a scrolled page can't feed back into
            // the height and walk it up or down.
            const topWithinDocument = scroller.getBoundingClientRect().top + window.scrollY
            const available = window.innerHeight - topWithinDocument - PANE_BOTTOM_GAP
            scroller.style.maxHeight = `${Math.max(PANE_MIN_HEIGHT, Math.round(available))}px`
        }

        fitPaneToViewport()
        update()
        scroller.addEventListener('scroll', update, { passive: true })
        window.addEventListener('resize', fitPaneToViewport)
        const observer = new ResizeObserver(update)
        observer.observe(scroller)
        return () => {
            scroller.removeEventListener('scroll', update)
            window.removeEventListener('resize', fitPaneToViewport)
            observer.disconnect()
        }
        // Re-measure whenever the scroller mounts or its contents change size.
    }, [loading, errorLoading, results.length])

    const nudgeTable = useCallback((distance: number) => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        tableScrollerRef.current?.scrollBy({
            left: distance,
            behavior: reduceMotion ? 'auto' : 'smooth',
        })
    }, [])

    // A screenful at a time, less a sliver so the row you were reading stays on
    // screen as an anchor.
    const nudgeTableDown = useCallback(() => {
        const scroller = tableScrollerRef.current
        if (!scroller) return
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        scroller.scrollBy({
            top: Math.round(scroller.clientHeight * 0.85),
            behavior: reduceMotion ? 'auto' : 'smooth',
        })
    }, [])

    const loadList = useCallback(async (forceFilter?: PassareFilterObject, page: number = 1) => {
        setErrorLoading(null)
        if (page === 1) {
            setLoading(true)
        } else {
            setLoadingMore(true)
        }

        try {
            const activeFilter = forceFilter || currentFilterObject || {
                selectedPassareOrganizationUniqueIds: [],
                onlyUnSyncedWith360: false,
                onlySyncedWith360: false,
                showAllLeadsEvenIfLocationIsIgnored: false,
            }
            
            // Add person search filter to the active filter
            const filterWithSearch = {
                ...activeFilter,
                person_search_filter: personSearchFilter || undefined
            }

            const limit = 100
            const {
                passare_acquaintances: result,
                total_passare_acquaintances: totalResult
            } = await getAllPassareAcquaintances({
                limit,
                page,
                filter: filterWithSearch
            })

            if (page === 1) {
                setResults(result)
            } else {
                setResults(prev => [...prev, ...result])
            }

            setTotalResults(totalResult)
            // Assume if we get a full page of results, there might be more
            setHasMore(result.length === limit)
        } catch (exception) {
            console.error(exception)
            const errorMessage =
                exception instanceof APIError ? exception.message : 'Failed to load leads'
            setErrorLoading(errorMessage)
        } finally {
            setLoading(false)
            setLoadingMore(false)
        }
    }, [currentFilterObject, personSearchFilter])

    useEffect(() => {
        // Load saved filters from local storage
        try {
            const savedFilter = localStorage.getItem(passareSaveFilterToLocalStorageKey)
            if (savedFilter) {
                const parsedFilter = JSON.parse(savedFilter)
                setCurrentFilterObject(parsedFilter)
                loadList(parsedFilter)
            } else {
                // Default filter
                const defaultFilter: PassareFilterObject = {
                    selectedPassareOrganizationUniqueIds: [],
                    onlyUnSyncedWith360: false,
                    onlySyncedWith360: false,
                    showAllLeadsEvenIfLocationIsIgnored: false,
                    showAllLeadsRegardlessOfContactQuality: false,
                }
                setCurrentFilterObject(defaultFilter)
                loadList(defaultFilter)
            }
        } catch (error) {
            console.error('Failed to load saved filter', error)
            // Fall back to defaults
            const defaultFilter: PassareFilterObject = {
                selectedPassareOrganizationUniqueIds: [],
                onlyUnSyncedWith360: false,
                onlySyncedWith360: false,
                showAllLeadsEvenIfLocationIsIgnored: false,
                showAllLeadsRegardlessOfContactQuality: false,
            }
            setCurrentFilterObject(defaultFilter)
            loadList(defaultFilter)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadMore = () => {
        if (hasMore && !loading && !loadingMore) {
            setCurrentPage(prevPage => prevPage + 1)
            loadList(currentFilterObject || undefined, currentPage + 1)
        }
    }

    // After a manual sync succeeds, patch the lead's ghl_contact in place from
    // the response (the backend returns the updated acquaintance) — no page
    // reload. Falls back to a reload only if the response shape is unexpected,
    // so the row never shows stale "unsynced" state for a lead that DID sync.
    const handleLeadSynced = useCallback((uniqueIdentifier: string, result: any) => {
        const ghlContact = result?.result?.private_data?.ghl_contact
        if (!ghlContact?.ghl_contact_id) {
            window.location.reload()
            return
        }
        setResults(prev => prev.map(lead => {
            if (lead.unique_identifier !== uniqueIdentifier) return lead
            // PassareAcquaintance is a class instance — update private_data on the
            // existing instance (spreading would drop its methods).
            lead.private_data = { ...lead.private_data, ghl_contact: ghlContact }
            return lead
        }))
    }, [])

    // Apply the (already debounced) search value from PassareSearchBox. This is
    // the ONLY place a search keystroke touches this component's state, so the
    // heavy results table is never re-rendered while the user is typing.
    const handleSearch = useCallback((value: string) => {
        setPersonSearchFilter(value);
        setCurrentPage(1);
    }, []);

    // Load list when search filter changes
    useEffect(() => {
        if (!isFirstRender.current) {
            loadList(currentFilterObject || undefined, 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personSearchFilter]);

    // Mark first render as complete
    useEffect(() => {
        isFirstRender.current = false;
    }, []);

    // Calculate unsynced leads
    const unsyncedLeads = useMemo(() => {
        return results.filter(lead => !lead.private_data.ghl_contact?.ghl_contact_id)
    }, [results]);

    const handleSyncAll = useCallback(() => {
        if (unsyncedLeads.length > 0) {
            setShowBulkSyncModal(true)
        }
    }, [unsyncedLeads]);

    // Once rows have scrolled up under the pinned header, lift it off them so
    // it reads as a layer with content beneath rather than the first row.
    const headerCellStyle: CSSProperties = useMemo(() => ({
        ...stickyHeaderCell,
        boxShadow: canScroll.up
            ? 'inset 0 -1px 0 #e4e6ef, 0 6px 8px -6px rgba(24, 28, 50, 0.18)'
            : stickyHeaderCell.boxShadow,
    }), [canScroll.up])

    const handleBulkSyncComplete = useCallback(() => {
        // Reload the list to refresh sync status
        loadList(currentFilterObject || undefined, 1)
        setShowBulkSyncModal(false)
    }, [loadList, currentFilterObject]);

    return <div>
        <PassareTableHeader
            title={totalResults === 1 ? '1 Passare Lead' : `${totalResults} Passare Leads`}
            filterLocalStorageKey={passareSaveFilterToLocalStorageKey}
            currentFilter={currentFilterObject}
            onApplyFilter={(filterObject) => {
                setCurrentFilterObject(filterObject)
                loadList(filterObject)
            }}
            onNavigateToManage={() => navigate('/debugging/all-leads/passare-acquaintances/manage')}
            personSearchFilter={personSearchFilter}
            onSyncAll={handleSyncAll}
            unsyncedLeadsCount={unsyncedLeads.length}
            searchBar={
                <PassareSearchBox
                    initialValue={personSearchFilter}
                    onSearch={handleSearch}
                />
            }
        />
        {errorLoading ? (
            <div className='p-10'>
                <div className="alert alert-danger" role="alert">
                    {errorLoading}
                </div>
                <div className='mt-5'>
                    <button className='btn btn-primary btn-lg' onClick={() => loadList()}>
                        Retry
                    </button>
                </div>
            </div>
        ) : (loading && currentPage === 1) ? (
            <div className='p-10'>
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        ) : (
        <div className='px-10'>
        <div className='position-relative'>
        {/* The column floors below add up to 700px, so the six columns fit a
            laptop-width card outright. Narrower than that, the pane scrolls
            sideways INSIDE the card rather than letting rows bleed past the
            card edge and drag the whole page under the fixed sidebar. */}
        <div className='table-responsive' style={tableScrollPane} ref={tableScrollerRef}>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                    <tr className='fw-bold text-muted'>
                        <th className='min-w-100px' style={headerCellStyle}>Updated At</th>
                        <th className='min-w-100px' style={headerCellStyle}>Organization</th>
                        <th className='min-w-150px' style={headerCellStyle}>Deceased</th>
                        <th className='min-w-100px' style={headerCellStyle}>Lead Name</th>
                        <th className='min-w-125px' style={headerCellStyle}>Contact Info</th>
                        <th className='min-w-125px' style={headerCellStyle}></th>
                    </tr>
                </thead>
                <tbody>
                    {results.map((acquaintance: PassareAcquaintance) => {
                        return (
                            <tr
                                key={`event-administrator-${acquaintance.unique_identifier}`}
                                data-unique-identifier={acquaintance.unique_identifier}
                            >
                                <td>
                                    {acquaintance.case_item.updated_at ? formatLeadItemDate(acquaintance.case_item.updated_at) : '?'}
                                    {/*
                                    // taking away signing because it is not reliable
                                    <br />
                                    {acquaintance.case_item.case_signed_at ? `Signed ${formatLeadItemDate(acquaintance.case_item.case_signed_at)}` : 'Never signed'}
                                    */}
                                </td>
                                {/*<td>{formatLeadItemDate(acquaintance.updated_at, true)}</td>*/}
                                <td>
                                    <Link to={`/debugging/all-leads/passare-acquaintances/organizations/${acquaintance.passare_organization.unique_identifier}`}>
                                        {acquaintance.passare_organization.organization_name || 'Unknown Organization'}
                                        {acquaintance.case_item.branch_name && <span>
                                            {` (${acquaintance.case_item.branch_name})`}
                                        </span>}
                                    </Link>
                                    {personSearchFilter && acquaintance.passare_organization.organization_name?.toLowerCase().includes(personSearchFilter.toLowerCase()) && (
                                        <span className='badge badge-light-info ms-2'>Organization Match</span>
                                    )}
                                </td>
                                {acquaintance.case_item.public_data.case_result ? (
                                    <>
                                        <td>
                                            <div>
                                                <Link to={`/passare/cases/${acquaintance.case_item.unique_identifier}`}>
                                                    {acquaintance.getDecedentFullName()}
                                                </Link>
                                                {personSearchFilter && acquaintance.getDecedentFullName()?.toLowerCase().includes(personSearchFilter.toLowerCase()) && (
                                                    <span className='badge badge-light-info ms-2'>Deceased Match</span>
                                                )}
                                            </div>
                                            <div>
                                                Born: {acquaintance.case_item.public_data.case_result.decedent_person?.person.birth_date}
                                            </div>
                                            {acquaintance.case_item.decedent_data && <div>
                                                Died: {acquaintance.case_item.decedent_data.death_date}
                                            </div>}
                                            {acquaintance.case_item.public_data.case_result?.client_service_type_name && <div>
                                                Client Service Type: {acquaintance.case_item.public_data.case_result.client_service_type_name}
                                            </div>}
                                            {acquaintance.case_item.case_signed_at && <div>
                                                Signing Date: {formatLeadItemDate(acquaintance.case_item.case_signed_at)}
                                            </div>}
                                            {acquaintance.case_item.disposition_data?.disposition_date && <div>
                                                Disposition Date: {acquaintance.case_item.disposition_data.disposition_date}
                                            </div>}
                                            {acquaintance.case_item.public_data.case_result.case_assigned_to && <div>
                                                Funeral Director: {acquaintance.case_item.public_data.case_result.case_assigned_to}
                                            </div>}
                                        </td>
                                    </>
                                ) : (
                                    // One cell only — the header has six columns, so emitting
                                    // two here gave un-hydrated cases a phantom 7th column and
                                    // shifted every later cell one column right.
                                    <td>-</td>
                                )}
                                <td>
                                    <Link to={`/passare/leads/${acquaintance.unique_identifier}`}>
                                        {acquaintance.public_data.acquaintance_result
                                            ? `${acquaintance.public_data.acquaintance_result.first_name || ''} ${acquaintance.public_data.acquaintance_result.last_name || ''}`.trim() || 'View lead'
                                            : 'View lead'}
                                    </Link>
                                    {acquaintance.public_data.acquaintance_result?.relationship && (
                                        <span> ({acquaintance.public_data.acquaintance_result.relationship})</span>
                                    )}
                                    {personSearchFilter && acquaintance.public_data.acquaintance_result &&
                                        `${acquaintance.public_data.acquaintance_result.first_name || ''} ${acquaintance.public_data.acquaintance_result.last_name || ''}`.toLowerCase().includes(personSearchFilter.toLowerCase()) && (
                                            <span className='badge badge-light-info ms-2'>Lead Name Match</span>
                                        )}
                                </td>
                                {/* text-break: a long email address is one unbreakable token
                                    and was forcing this column ~250px wide on its own. */}
                                <td className='text-break'>
                                    {acquaintance.public_data.phone_numbers &&
                                        (
                                            acquaintance.public_data.phone_numbers.home_phone ||
                                            acquaintance.public_data.phone_numbers.mobile_phone ||
                                            acquaintance.public_data.phone_numbers.work_phone
                                        ) && (
                                            <>
                                                {acquaintance.public_data.phone_numbers.home_phone && (
                                                    <div>Home: {acquaintance.public_data.phone_numbers.home_phone}</div>
                                                )}
                                                {acquaintance.public_data.phone_numbers.mobile_phone && (
                                                    <div>Mobile: {acquaintance.public_data.phone_numbers.mobile_phone}</div>
                                                )}
                                                {acquaintance.public_data.phone_numbers.work_phone && (
                                                    <div>Work: {acquaintance.public_data.phone_numbers.work_phone}</div>
                                                )}
                                            </>
                                        )}
                                    {acquaintance.public_data.email_address && (
                                        <div>Email: {acquaintance.public_data.email_address}</div>
                                    )}
                                </td>
                                <td>
                                    {acquaintance.private_data.ghl_contact?.ghl_contact_id ? <div>
                                        <div>
                                            <a href={
                                                `https://app.premier360platform.com/v2/location/${acquaintance.private_data.ghl_contact.ghl_location_id}/contacts/detail/${acquaintance.private_data.ghl_contact.ghl_contact_id}`
                                            } target='_blank' rel='noreferrer'>
                                                View in FRM
                                            </a>
                                        </div>
                                        <div className='mt-2'>
                                            {/* Sync to 360 */}
                                            <SyncLeadButton uniqueIdentifier={acquaintance.unique_identifier} isSecondary onSynced={(result) => handleLeadSynced(acquaintance.unique_identifier, result)} />
                                        </div>
                                    </div> : <>
                                        <div className='mt-2'>
                                            {/* Sync to 360 */}
                                            <SyncLeadButton uniqueIdentifier={acquaintance.unique_identifier} onSynced={(result) => handleLeadSynced(acquaintance.unique_identifier, result)} />
                                        </div>
                                    </>}
                                </td>
                            </tr>
                        )
                    })}
                    {!loading && hasMore && (
                        <tr>
                            <td colSpan={6}>
                                <button
                                    disabled={loadingMore}
                                    onClick={loadMore}
                                    className='btn btn-primary'
                                >
                                    {loadingMore ? 'Loading...' : 'Load More'}
                                </button>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
        {/* Which way the table still has content. The bottom fade is the
            vertical cue; left/right pair with the chevrons. The pinned header
            paints over these where they meet, so labels stay crisp. */}
        {canScroll.down && <div aria-hidden='true' style={scrollFade('bottom')} />}
        {canScroll.left && <div aria-hidden='true' style={scrollFade('left')} />}
        {canScroll.right && <div aria-hidden='true' style={scrollFade('right')} />}
        {/* macOS hides overlay scrollbars at rest, so sideways scroll needs a
            control you can actually see. Anchored to the pane's edges rather
            than floating over the page — it belongs to this table. */}
        {canScroll.left && (
            <button
                type='button'
                aria-label='Show previous columns'
                className='btn btn-icon btn-sm btn-light-primary rounded-circle shadow-sm'
                style={{ ...scrollNudgeButton, left: '-0.9rem' }}
                onClick={() => nudgeTable(-320)}
            >
                <i className='bi bi-chevron-left'></i>
            </button>
        )}
        {canScroll.right && (
            <button
                type='button'
                aria-label='Show more columns'
                className='btn btn-icon btn-sm btn-light-primary rounded-circle shadow-sm'
                style={{ ...scrollNudgeButton, right: '-0.9rem' }}
                onClick={() => nudgeTable(320)}
            >
                <i className='bi bi-chevron-right'></i>
            </button>
        )}
        {canScroll.down && (
            <button
                type='button'
                aria-label='Show more rows'
                className='btn btn-icon btn-sm btn-light-primary rounded-circle shadow-sm'
                style={scrollNudgeButtonDown}
                onClick={nudgeTableDown}
            >
                <i className='bi bi-chevron-down'></i>
            </button>
        )}
        </div>
        </div>
        )}

        <BulkSyncModal
            isOpen={showBulkSyncModal}
            onClose={() => setShowBulkSyncModal(false)}
            leadsToSync={unsyncedLeads}
            onSyncComplete={handleBulkSyncComplete}
        />
    </div>
}

function PassareManagementWrapper() {
    const navigate = useNavigate()
    
    return (
        <div>
            <PassareTableHeader
                title="Manage Passare Organizations"
                filterLocalStorageKey={passareSaveFilterToLocalStorageKey}
                currentFilter={null}
                onApplyFilter={() => {}}
                onNavigateBack={() => navigate('/debugging/all-leads/passare-acquaintances')}
            />
            <div className="pt-5">
                <PassareManagement isEmbedded={true} />
            </div>
        </div>
    )
}

function PassareOrganizationDetailWrapper() {
    const navigate = useNavigate()
    
    return (
        <div>
            <PassareTableHeader
                title="Passare Organization Details"
                filterLocalStorageKey={passareSaveFilterToLocalStorageKey}
                currentFilter={null}
                onApplyFilter={() => {}}
                onNavigateBack={() => navigate('/debugging/all-leads/passare-acquaintances/manage')}
            />
            <div className="pt-5">
                <PassareOrganizationDetail isEmbedded={true} />
            </div>
        </div>
    )
}

export function PassareAcquaintancesTable() {
    return (
        <Routes>
            <Route index element={<PassareTable />} />
            <Route path="manage" element={<PassareManagementWrapper />} />
            <Route path="organizations/:passare_organization_unique_identifier" element={<PassareOrganizationDetailWrapper />} />
            <Route path="*" element={<Navigate to="/debugging/all-leads/passare-acquaintances" />} />
        </Routes>
    )
}